import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, FolderOpen, Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import * as TeamsModule from 'modules/teams';
import * as CollectionsModule from 'modules/collections';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { GroupProfile, GroupProfileSkeleton } from './GroupProfile';
import { AssignedCollectionGroup } from './AssignedCollectionGroup';
import { AssignCollectionDialog } from './AssignCollectionDialog';

/** Groups are assigned *collections* (which bundle tests), not individual tests. */
export const TeamCollectionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const [assignOpen, setAssignOpen] = useState(false);

  const {
    team,
    isLoading: teamLoading,
    isError: teamIsError,
    refetch: refetchTeam,
  } = TeamsModule.Hooks.useSingle({ id: teamId || '', enabled: Boolean(teamId) });
  const {
    collections,
    isLoading,
    isError: collectionsIsError,
    refetch,
  } = CollectionsModule.Hooks.useByTeam({
    teamId: teamId || '',
    enabled: Boolean(teamId),
  });
  const canManage = usePermission(PERMISSIONS.TEAMS_EDIT);

  if (teamIsError) {
    return (
      <EmptyState
        icon={<AlertCircle size={28} />}
        title={t('failed_to_load')}
        secondaryAction={{ label: t('retry'), onClick: () => refetchTeam() }}
      />
    );
  }

  if (teamLoading || !team) {
    return <GroupProfileSkeleton />;
  }

  const openAssign = () => setAssignOpen(true);
  const actions = canManage && (
    <Button size="sm" className="gap-1.5" onClick={openAssign}>
      <Plus className="h-4 w-4" />
      {t('group_assign_collection')}
    </Button>
  );

  return (
    <GroupProfile team={team} activeTab="collections" actions={actions}>
      {collectionsIsError ? (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      ) : isLoading ? (
        <div className="space-y-4" aria-hidden>
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full rounded-lg" />
          ))}
        </div>
      ) : collections.length === 0 ? (
        <EmptyState
          icon={<FolderOpen size={28} />}
          title={t('assigned_collections_empty_title')}
          description={t('assigned_collections_empty_desc')}
          action={
            canManage
              ? { label: t('group_assign_collection'), icon: <Plus className="h-4 w-4" />, onClick: openAssign }
              : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          {collections.map((c) => (
            <AssignedCollectionGroup key={c.id} collection={c} />
          ))}
        </div>
      )}

      <AssignCollectionDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        teamId={teamId || ''}
        assignedCollectionIds={collections.map((c) => c.id)}
        onAssigned={() => refetch()}
      />
    </GroupProfile>
  );
};

export default TeamCollectionsPage;
